import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import { selectAccessToken } from '../../../slices/staffSlice';
import { show } from '../../../slices/toastSlice';
import { Loader } from '../../../components';
import { HugeiconsIcon } from '@hugeicons/react';
import { Download01Icon } from '@hugeicons/core-free-icons';

// Streams a populated evaluation workbook from loan-service and triggers a
// browser download. The workbook contains all evaluation sheets (KYC, credit
// bureau, bank review, MoMo, DTI, risk score, pension/collateral, fraud,
// decision memo, risk-manager review) with the loan's data filled in.
//
// The endpoint requires an auth token, so we fetch with the Authorization
// header and synthesize a download via a Blob URL.
function DownloadTemplateButton({ loanId, label }) {
    const accessToken = useSelector(selectAccessToken);
    const dispatch = useDispatch();
    const [downloading, setDownloading] = useState(false);

    const handleDownload = async () => {
        if (!loanId) {
            dispatch(show({ type: 'error', state: true, message: 'Missing loan ID' }));
            return;
        }
        setDownloading(true);
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_STAGING}/loans/staff/evaluation/export/${encodeURIComponent(loanId)}`,
                {
                    responseType: 'blob',
                    headers: { authorization: accessToken },
                }
            );

            // pull filename from Content-Disposition if the server set one,
            // otherwise fall back to a sensible default
            const cd = res.headers['content-disposition'] || '';
            const match = /filename="?([^"]+)"?/i.exec(cd);
            const filename = match ? match[1] : `Evaluation_${loanId}.xlsx`;

            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            dispatch(show({ type: 'error', state: true, message: 'Failed to download evaluation' }));
        } finally {
            setDownloading(false);
        }
    };

    return (
        <button
            type="button"
            onClick={handleDownload}
            disabled={downloading || !loanId}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#1e3a6e] bg-white border border-[#1e3a6e]/20 rounded-lg hover:bg-blue-50 disabled:opacity-50 transition-colors"
        >
            {downloading ? <Loader color="text-[#1e3a6e]" size="w-3.5 h-3.5" /> : <HugeiconsIcon icon={Download01Icon} size={13} />}
            {downloading ? 'Generating…' : (label || 'Download Evaluation')}
        </button>
    );
}

export default DownloadTemplateButton;
